import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import dayjs from "dayjs";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Button, Chip, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { statusColors, statusLabels } from "@/features/sales-invoices";
import { apiOutletClient, formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import { CustomerSelect } from "@/features/customers";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const SalesDownPayments = () => {
  const { outlet, changeOutlet, hasPermission, isHubOutlet } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [salesDownPayments, setSalesDownPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  const buyer = useMemo(() => {
    const buyerParam = searchParams.get("buyer");
    if (!buyerParam) return null;
    try {
      return JSON.parse(buyerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadSalesDownPayments = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("sales-down-payments", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          buyerableId: buyer?.id || null,
          withBuyer: true,
          withCreator: true,
        },
      });
      setSalesDownPayments(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat uang muka penjualan"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    buyer,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadSalesDownPayments();
  }, [loadSalesDownPayments]);

  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("buyer");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleBuyerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "buyer",
          JSON.stringify({ id: value.id, name: value.name }),
        );
      } else {
        newParams.delete("buyer");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  const toolbarContent = hasPermission(
    PermissionEnum.CREATE_SALES_DOWN_PAYMENTS,
  ) && (
    <Button
      variant="contained"
      component={Link}
      to="create"
      disabled={!isHubOutlet}
    >
      Tambah
    </Button>
  );

  return (
    <PageContainer
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <AllOutletsSelect
                value={buyer}
                onChange={handleBuyerChange}
                allowedRoles={[OutletRoleEnum.RETAIL]}
                TextFieldProps={{
                  label: "Pelanggan (Retail)",
                }}
              />
            ) : (
              <CustomerSelect
                value={buyer}
                onChange={handleBuyerChange}
                TextFieldProps={{
                  label: "Pelanggan (Customer)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Penjual",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={salesDownPayments}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              valueGetter: (transactionDate) => {
                const today = dayjs().startOf("day");
                const transaction = dayjs(transactionDate).startOf("day");
                const age = today.diff(transaction, "day");
                return age > 0
                  ? `${formatDate(transactionDate)} (${age}d)`
                  : formatDate(transactionDate);
              },
            },
            {
              field: "creator",
              headerName: "Dibuat Oleh",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value?.name ?? "-",
            },
            {
              field: "buyer",
              headerName: "Pelanggan",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value.name,
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1,
              sortable: false,
              valueGetter: (value) => value ?? "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={statusLabels[row.status]}
                  color={statusColors[row.status]}
                />
              ),
            },
            {
              field: "grandTotal",
              headerName: "Total",
              flex: 1,
              sortable: false,
              type: "number",
              valueGetter: (value, row) => formatNumber(row.grandTotal),
            },
            ...(hasPermission(PermissionEnum.READ_SALES_DOWN_PAYMENTS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      <IconButton
                        key="view"
                        onClick={() =>
                          navigate(`${params.row.referenceNumber}`)
                        }
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>,
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
